import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { BranchService, buildPersonalIdValidation, ClassificationCodeService, LocationService, NotificationService, UserService, UserSourceService, toCalendarDate } from '@beautyapp/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DatepickerComponent, InputComponent, SelectComponent, TextareaComponent } from '@beautyapp/form';
import { TranslateModule } from '@ngx-translate/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'beautyapp-add-appointment-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzModalModule,
    NzGridModule,
    NzButtonModule,
    NzCheckboxModule,
    TranslateModule,
    InputComponent,
    SelectComponent,
    DatepickerComponent,
    TextareaComponent
  ]
})
export class AddUserComponent implements OnDestroy, OnInit {
  modal = inject(NzModalRef);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private fb = inject(UntypedFormBuilder);

  readonly data = inject(NZ_MODAL_DATA);
  readonly classificationCodeService = inject(ClassificationCodeService);
  readonly userSourceService = inject(UserSourceService);
  private readonly locationService = inject(LocationService);
  private store = inject(Store);
  private branchService = inject(BranchService);

  // Personal-ID validation is driven by the per-branch config (beaut-315):
  // { enabled, required, minLength, maxLength }. Applied dynamically in ngOnInit.
  personalIdRequired = false;
  personalIdError = 'validators.personalId11Digits';

  genders = this.classificationCodeService.getGender();
  bloodGroups = this.classificationCodeService.getBloodGroup();
  smsLanguages = [
    { id: 'ka', value: 'ქართული' },
    { id: 'ru', value: 'რუსული' },
    { id: 'en', value: 'ინგლისური' },
  ];
  userSources = toSignal(this.userSourceService.dropdown(), { initialValue: [] });
  countries = toSignal(this.locationService.getCountries(), { initialValue: [] });
  cities = toSignal(this.locationService.getCities({}), { initialValue: [] });

  get userId() {
    return this.data.userId;
  }

  form: UntypedFormGroup;
  sub$: Subject<any> = new Subject<any>();
  userVerification = signal<boolean>(false);

  constructor() {
    this.form = this.fb.group({
      cardNo: null,
      firstname: [null, Validators.required],
      lastname: [null],
      gender: null,
      mobilePhone: [null, Validators.required],
      // Validators applied dynamically from the branch personal-id config (ngOnInit).
      identityNumber: [null, []],
      birthDate: null,
      description: [null],
      countryId: null,
      cityId: null,
      address: null,
      bloodGroup: null,
      workplace: null,
      userSourceId: null,
      smsLanguage: null
    });

    // Default-select the pinned first city (Tbilisi) on create, not on edit.
    effect(() => {
      const list = this.cities();
      if (!this.data?.user && list?.length && !this.form.get('cityId')?.value) {
        this.form.patchValue({ cityId: list[0].id }, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    if (this.data?.user) {
      this.form.patchValue({
        ...this.data.user,
        gender: +this.data.user.gender
      });
    }

    const branch = this.store.selectSnapshot(AuthState.getActiveBranch);
    if (branch?.id) {
      this.branchService.getPersonalIdConfig(branch.id).subscribe({
        next: (cfg) => this.applyPersonalIdConfig(cfg),
        error: () =>
          this.applyPersonalIdConfig({
            enabled: !!branch?.personalIdValidationEnabled,
            required: true,
            minLength: 11,
            maxLength: 11,
          }),
      });
    }
  }

  private applyPersonalIdConfig(cfg: any): void {
    const { validators, required, error } = buildPersonalIdValidation(cfg);
    this.personalIdRequired = required;
    this.personalIdError = error;
    const ctrl = this.form.get('identityNumber');
    ctrl?.setValidators(validators);
    ctrl?.updateValueAndValidity();
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.userService
      .create({ ...this.form.value, birthDate: toCalendarDate(this.form.value.birthDate) })
      .pipe(takeUntil(this.sub$))
      .subscribe({
        next: (res) => this.modal.close(res),
        error: (err) => {
          const message = err?.error?.message || err?.message;
          if (message) {
            this.notificationService.warning(message, 'პირადი ნომერი');
          }
        },
      });
  }

  checkUser() {
    const { mobilePhone } = this.form.value;
    if (mobilePhone && mobilePhone.length >= 9) {
      this.userService
        .checkUser({
          mobilePhone
        })
        .pipe(takeUntil(this.sub$))
        .subscribe((res) => {
          if (res) {
            this.notificationService.warning(
              'მსგავსი მობლურის ნორმით მომხმარებელი უკვე არსებობს',
              'მომხმარებლის ნომრის შემოწმება'
            );
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }
}
